export interface ScreenshotResult {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
  diagnostics?: ScreenshotDiagnostics;
}

/**
 * Diagnostic information for debugging screenshot issues
 */
export interface ScreenshotDiagnostics {
  captureMethod: 'standard' | 'body' | 'fallback' | 'native';
  preflightPassed: boolean;
  postflightPassed: boolean;
  warnings: string[];
  pageInfo: {
    url: string;
    title: string;
    doctype: string;
    scrollPosition: { x: number; y: number };
    viewportSize: { width: number; height: number };
    documentSize: { width: number; height: number };
    bodyStyles: Record<string, string>;
    htmlStyles: Record<string, string>;
  };
  timing: {
    preflightMs: number;
    captureMs: number;
    postflightMs: number;
    totalMs: number;
  };
  errors: string[];
}

/**
 * Screenshot capture method preference
 * - 'auto': Try DOM-based first, fall back to native if issues detected
 * - 'native': Use Screen Capture API (requires user permission)
 * - 'dom': Use DOM cloning (modern-screenshot) - may have scroll/shadow DOM issues
 */
export type ScreenshotCaptureMethod = 'auto' | 'native' | 'dom';

/**
 * Screenshot capture states for the async background capture system
 */
export type ScreenshotCaptureState = 'idle' | 'capturing' | 'completed' | 'failed' | 'cancelled';

/**
 * Progress information for screenshot capture
 */
export interface ScreenshotProgress {
  phase: 'preflight' | 'capture' | 'crop' | 'postflight';
  progress: number; // 0-100
  message: string;
}

/**
 * Callback type for state change notifications
 */
export type ScreenshotStateCallback = (
  state: ScreenshotCaptureState,
  result?: ScreenshotResult,
  error?: Error
) => void;

/**
 * Callback type for progress notifications
 */
export type ScreenshotProgressCallback = (progress: ScreenshotProgress) => void;

/**
 * Configuration for screenshot capture with enhanced options
 */
interface CaptureConfig {
  debug: boolean;
  maxRetries: number;
  timeoutMs: number;
  minImageSizeBytes: number;
  minNonWhitePixelPercent: number;
  maxScale: number;
  /** Preferred capture method: 'auto', 'native', or 'dom' */
  preferredMethod: ScreenshotCaptureMethod;
}

/**
 * Check if the current device is iOS (iPhone, iPad, iPod)
 * Note: On iOS, all browsers (Safari, Chrome, Firefox, Edge) use WebKit
 * and none support the Screen Capture API (getDisplayMedia)
 */
export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor || '';

  // Check for iOS devices
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return true;
  }

  // iPad on iOS 13+ reports as Mac, check for touch support
  if (/Macintosh/.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
}

/**
 * Check if the current device is Android
 * Android browsers also don't support getDisplayMedia
 */
export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/**
 * Check if the device is mobile (iOS or Android)
 * Mobile browsers don't support Screen Capture API
 */
export function isMobileDevice(): boolean {
  return isIOSDevice() || isAndroidDevice();
}

// Re-export for backwards compatibility - already exported above

/**
 * ScreenshotManager - Manages async background screenshot capture
 *
 * This class enables starting screenshot capture immediately when the feedback
 * dialog opens, allowing the user to decide later whether to attach it.
 *
 * Features:
 * - Background capture that doesn't block UI
 * - Promise-based result access (attach to ongoing capture)
 * - Graceful cancellation via AbortController
 * - State change notifications
 * - Automatic cleanup on cancel/error
 * - Pre-flight and post-flight validation
 * - Multiple capture strategy fallbacks
 * - Comprehensive diagnostics
 * - Native Screen Capture API support for pixel-perfect screenshots
 * - Automatic fallback to DOM-based capture on iOS/Android (no getDisplayMedia support)
 */
export class ScreenshotManager {
  private state: ScreenshotCaptureState = 'idle';
  private result: ScreenshotResult | null = null;
  private error: Error | null = null;
  private capturePromise: Promise<ScreenshotResult> | null = null;
  private abortController: AbortController | null = null;
  private stateCallbacks: Set<ScreenshotStateCallback> = new Set();
  private progressCallbacks: Set<ScreenshotProgressCallback> = new Set();

  // Capture configuration
  private config: CaptureConfig = {
    debug: false,
    maxRetries: 3,
    timeoutMs: 20000,
    minImageSizeBytes: 1000, // Minimum valid image size
    minNonWhitePixelPercent: 1, // At least 1% non-white pixels
    maxScale: 1.5,
    preferredMethod: 'dom', // Default to DOM-based for automatic capture
  };

  /**
   * Enable or disable debug mode
   */
  public setDebugMode(enabled: boolean): void {
    this.config.debug = enabled;
    if (enabled) {
      console.log('[KERN Screenshot] Debug mode enabled');
    }
  }

  /**
   * Set the preferred capture method
   * - 'native': Use Screen Capture API (requires user permission) - only method now supported
   * - 'auto' and 'dom': Deprecated, will use native capture
   *
   * Note: On iOS/Android devices, screenshot capture is not supported at all.
   * The UI should show a file upload option instead.
   */
  public setCaptureMethod(method: ScreenshotCaptureMethod): void {
    // On mobile devices, screenshot capture is not supported
    if (isMobileDevice()) {
      if (this.config.debug) {
        console.log(
          '[KERN Screenshot] Mobile device detected - screenshot capture not supported. ' +
            'Use file upload instead.'
        );
      }
      return;
    }

    // All methods now use native capture
    this.config.preferredMethod = 'native';
    if (this.config.debug) {
      console.log(`[KERN Screenshot] Capture method set to: native (requested: ${method})`);
    }
  }

  /**
   * Get the current capture state
   */
  public getState(): ScreenshotCaptureState {
    return this.state;
  }

  /**
   * Get the captured result (if available)
   */
  public getResult(): ScreenshotResult | null {
    return this.result;
  }

  /**
   * Get the error (if capture failed)
   */
  public getError(): Error | null {
    return this.error;
  }

  /**
   * Check if capture is currently in progress
   */
  public isCapturing(): boolean {
    return this.state === 'capturing';
  }

  /**
   * Check if a result is available
   */
  public hasResult(): boolean {
    return this.state === 'completed' && this.result !== null;
  }

  /**
   * Subscribe to state changes
   */
  public onStateChange(callback: ScreenshotStateCallback): () => void {
    this.stateCallbacks.add(callback);
    // Return unsubscribe function
    return () => this.stateCallbacks.delete(callback);
  }

  /**
   * Subscribe to progress updates during capture
   */
  public onProgress(callback: ScreenshotProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    // Return unsubscribe function
    return () => this.progressCallbacks.delete(callback);
  }

  /**
   * Start background screenshot capture using DOM cloning (default method)
   *
   * This method starts the capture process immediately and returns.
   * Use `waitForResult()` or `onStateChange()` to get the result.
   *
   * @returns true if capture started, false if already capturing or on mobile
   */
  public startCapture(): boolean {
    // Don't start if already capturing
    if (this.state === 'capturing') {
      return false;
    }

    // On mobile devices, screenshot capture is not supported
    // The UI should show a file upload option instead
    if (isMobileDevice()) {
      if (this.config.debug) {
        console.log(
          '[KERN Screenshot] Mobile device detected - screenshot capture not supported. ' +
            'Use file upload instead.'
        );
      }
      this.error = new Error(
        'Screenshot capture is not supported on mobile devices. Please use file upload.'
      );
      this.setState('failed', undefined, this.error);
      return false;
    }

    // Reset state
    this.result = null;
    this.error = null;
    this.abortController = new AbortController();
    this.setState('capturing');

    // Always use native capture (Screen Capture API)
    this.capturePromise = this.performNativeCapture();

    // Handle completion
    this.capturePromise
      .then((result) => {
        if (this.state === 'capturing') {
          // Not cancelled
          this.result = result;
          this.setState('completed', result);
        }
      })
      .catch((err) => {
        if (this.state === 'capturing') {
          // Not cancelled
          this.error = err instanceof Error ? err : new Error(String(err));
          this.setState('failed', undefined, this.error);
        }
      });

    return true;
  }

  /**
   * Start native pixel-based screenshot capture using Screen Capture API
   *
   * This requires user interaction and permission. The browser will show a dialog
   * for the user to select which tab/window to capture.
   *
   * Note: On iOS/Android, this method is not supported. The UI should show
   * a file upload option instead.
   *
   * @returns true if capture started, false if already capturing or on mobile
   */
  public startNativeCapture(): boolean {
    // This is now the same as startCapture since we only support native capture
    return this.startCapture();
  }

  /**
   * Wait for the current capture to complete
   *
   * If capture is in progress, this will wait for it.
   * If capture is completed, returns immediately.
   * If capture failed or was cancelled, throws an error.
   * If no capture started, starts one automatically.
   *
   * @throws Error if capture fails or is cancelled
   */
  public async waitForResult(): Promise<ScreenshotResult> {
    // If already completed, return cached result
    if (this.state === 'completed' && this.result) {
      return this.result;
    }

    // If failed, throw cached error
    if (this.state === 'failed' && this.error) {
      throw this.error;
    }

    // If cancelled, throw error
    if (this.state === 'cancelled') {
      throw new Error('Screenshot capture was cancelled');
    }

    // If idle, start capture
    if (this.state === 'idle') {
      this.startCapture();
    }

    // Wait for the promise
    if (this.capturePromise) {
      return this.capturePromise;
    }

    throw new Error('No capture in progress');
  }

  /**
   * Cancel the current capture
   */
  public cancel(): void {
    if (this.state === 'capturing') {
      this.abortController?.abort();
      this.setState('cancelled');
      this.cleanup();
    }
  }

  /**
   * Reset the manager to idle state, clearing any results
   */
  public reset(): void {
    this.cancel();
    this.result = null;
    this.error = null;
    this.capturePromise = null;
    this.abortController = null;
    this.setState('idle');
  }

  /**
   * Clear the result without resetting capture state
   */
  public clearResult(): void {
    this.result = null;
    if (this.state === 'completed') {
      this.setState('idle');
    }
  }

  private setState(
    newState: ScreenshotCaptureState,
    result?: ScreenshotResult,
    error?: Error
  ): void {
    this.state = newState;
    // Notify all subscribers
    this.stateCallbacks.forEach((callback) => {
      try {
        callback(newState, result, error);
      } catch (e) {
        console.error('Error in screenshot state callback:', e);
      }
    });
  }

  /**
   * Report progress to all subscribers
   */
  private reportProgress(
    phase: ScreenshotProgress['phase'],
    progress: number,
    message: string
  ): void {
    const progressInfo: ScreenshotProgress = { phase, progress, message };
    this.progressCallbacks.forEach((callback) => {
      try {
        callback(progressInfo);
      } catch (e) {
        console.error('Error in screenshot progress callback:', e);
      }
    });
  }

  private cleanup(): void {
    // Nothing to cleanup - elements are filtered out during capture, not hidden
  }

  /**
   * Perform native pixel-based screenshot capture using Screen Capture API
   *
   * This captures actual pixels from the screen - no DOM manipulation needed.
   * Works perfectly regardless of scroll position, sticky headers, shadow DOM, etc.
   */
  private async performNativeCapture(): Promise<ScreenshotResult> {
    const signal = this.abortController?.signal;
    const startTime = performance.now();

    // Check for abort before starting
    if (signal?.aborted) {
      throw new Error('Screenshot capture was cancelled');
    }

    // Check if Screen Capture API is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      throw new Error('Screen Capture API is not supported in this browser');
    }

    this.reportProgress('capture', 10, 'Berechtigung wird angefordert...');

    let stream: MediaStream | null = null;

    try {
      // Request screen capture permission - browser will show a dialog
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
        },
        audio: false,
        // @ts-expect-error - preferCurrentTab is experimental but widely supported
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      });

      if (signal?.aborted) {
        throw new Error('Screenshot capture was cancelled');
      }

      this.reportProgress('capture', 30, 'Screenshot wird erstellt...');

      // Get the video track
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        throw new Error('No video track available');
      }

      // Get the actual video dimensions from the track settings
      const settings = videoTrack.getSettings();
      const width = settings.width || window.innerWidth;
      const height = settings.height || window.innerHeight;

      // Create a video element to capture the frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      // Wait for the video to be ready
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => {
          video.play().then(resolve).catch(reject);
        };
        video.onerror = () => reject(new Error('Failed to load video stream'));
        setTimeout(() => reject(new Error('Video load timeout')), 5000);
      });

      if (signal?.aborted) {
        throw new Error('Screenshot capture was cancelled');
      }

      this.reportProgress('capture', 60, 'Bild wird verarbeitet...');

      // Small delay to ensure frame is rendered
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Create canvas and capture the frame
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Draw the current video frame to canvas
      ctx.drawImage(video, 0, 0, width, height);

      this.reportProgress('postflight', 80, 'Screenshot wird finalisiert...');

      // Convert to blob and data URL
      const dataUrl = canvas.toDataURL('image/png', 0.92);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to create blob'))),
          'image/png',
          0.92
        );
      });

      const totalMs = performance.now() - startTime;

      this.reportProgress('postflight', 100, 'Fertig!');

      return {
        blob,
        dataUrl,
        width,
        height,
        diagnostics: {
          captureMethod: 'native',
          preflightPassed: true,
          postflightPassed: true,
          warnings: ['Captured using native Screen Capture API'],
          pageInfo: {
            url: window.location.href,
            title: document.title,
            doctype: document.doctype ? `<!DOCTYPE ${document.doctype.name}>` : 'none',
            scrollPosition: { x: window.scrollX, y: window.scrollY },
            viewportSize: { width: window.innerWidth, height: window.innerHeight },
            documentSize: {
              width: document.documentElement.scrollWidth,
              height: document.documentElement.scrollHeight,
            },
            bodyStyles: {},
            htmlStyles: {},
          },
          timing: { preflightMs: 0, captureMs: totalMs, postflightMs: 0, totalMs },
          errors: [],
        },
      };
    } finally {
      // Always stop all tracks to release the screen capture
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  }
}

/**
 * ScrollLock - Utility to lock/unlock page scrolling
 *
 * Used to prevent the viewport from scrolling while the feedback dialog
 * is open, ensuring consistent screenshot capture.
 */
export class ScrollLock {
  private static instance: ScrollLock | null = null;
  private isLocked = false;
  private originalStyles: {
    overflow: string;
    position: string;
    top: string;
    left: string;
    width: string;
    height: string;
  } | null = null;
  private scrollPosition = { x: 0, y: 0 };
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  /**
   * Get the singleton instance
   */
  public static getInstance(): ScrollLock {
    if (!ScrollLock.instance) {
      ScrollLock.instance = new ScrollLock();
    }
    return ScrollLock.instance;
  }

  /**
   * Check if scrolling is currently locked
   */
  public getIsLocked(): boolean {
    return this.isLocked;
  }

  /**
   * Lock page scrolling
   * Saves the current scroll position and prevents all scroll interactions
   */
  public lock(): void {
    if (this.isLocked) return;

    // Save current scroll position
    this.scrollPosition = {
      x: window.scrollX || window.pageXOffset,
      y: window.scrollY || window.pageYOffset,
    };

    // Save original body styles
    const body = document.body;
    const html = document.documentElement;

    this.originalStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      height: body.style.height,
    };

    // Apply scroll lock styles
    // Using fixed position approach for maximum compatibility
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollPosition.y}px`;
    body.style.left = `-${this.scrollPosition.x}px`;
    body.style.width = '100%';
    html.style.overflow = 'hidden';

    // Prevent touch scrolling
    this.touchMoveHandler = (e: TouchEvent) => {
      // Allow scrolling within the dialog itself
      const target = e.target as HTMLElement;
      if (target.closest('.kern-dialog')) {
        return;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', this.touchMoveHandler, { passive: false });

    // Prevent wheel scrolling on body
    this.wheelHandler = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.kern-dialog')) {
        return;
      }
      e.preventDefault();
    };
    document.addEventListener('wheel', this.wheelHandler, { passive: false });

    // Prevent keyboard scrolling (arrow keys, space, page up/down)
    this.keyHandler = (e: KeyboardEvent) => {
      const scrollKeys = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Space',
        'PageUp',
        'PageDown',
        'Home',
        'End',
      ];
      const target = e.target as HTMLElement;

      // Allow keyboard navigation within the dialog
      if (target.closest('.kern-dialog')) {
        return;
      }

      if (scrollKeys.includes(e.code)) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', this.keyHandler);

    this.isLocked = true;
  }

  /**
   * Unlock page scrolling
   * Restores the original scroll position and allows scroll interactions
   */
  public unlock(): void {
    if (!this.isLocked) return;

    const body = document.body;
    const html = document.documentElement;

    // Restore original styles
    if (this.originalStyles) {
      body.style.overflow = this.originalStyles.overflow;
      body.style.position = this.originalStyles.position;
      body.style.top = this.originalStyles.top;
      body.style.left = this.originalStyles.left;
      body.style.width = this.originalStyles.width;
      html.style.overflow = '';
    }

    // Restore scroll position
    window.scrollTo(this.scrollPosition.x, this.scrollPosition.y);

    // Remove event listeners
    if (this.touchMoveHandler) {
      document.removeEventListener('touchmove', this.touchMoveHandler);
      this.touchMoveHandler = null;
    }
    if (this.wheelHandler) {
      document.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }

    this.isLocked = false;
    this.originalStyles = null;
  }
}

/**
 * Erstellt einen Screenshot des aktuellen Viewports
 *
 * @deprecated Use ScreenshotManager for better async handling
 */
export async function captureScreenshot(): Promise<ScreenshotResult> {
  const manager = new ScreenshotManager();
  return manager.waitForResult();
}

/**
 * Konvertiert ein File/Blob zu Base64 Data URL
 */
export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Konvertiert Base64 Data URL zu Blob
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }

  return new Blob([array], { type: mime });
}

/**
 * Native pixel-based screenshot capture using Screen Capture API
 *
 * This is the simplest and most robust screenshot solution:
 * - Captures exact pixels as rendered by the browser
 * - Works regardless of scroll position, sticky headers, or shadow DOM
 * - No DOM cloning or complex CSS recreation needed
 *
 * Drawbacks:
 * - Requires user permission (browser shows a dialog to select tab/window)
 * - User must confirm the capture
 *
 * @returns Promise<ScreenshotResult> - The captured screenshot
 * @throws Error if user denies permission or API is not supported
 */
export async function captureNativeScreenshot(): Promise<ScreenshotResult> {
  // Check if Screen Capture API is supported
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    throw new Error('Screen Capture API is not supported in this browser');
  }

  let stream: MediaStream | null = null;

  try {
    // Request screen capture permission - browser will show a dialog
    // preferCurrentTab hints to the browser to pre-select the current tab
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: 'browser', // Prefer browser tab
      },
      audio: false,
      // @ts-expect-error - preferCurrentTab is experimental but widely supported
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    });

    // Get the video track
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      throw new Error('No video track available');
    }

    // Get the actual video dimensions from the track settings
    const settings = videoTrack.getSettings();
    const width = settings.width || window.innerWidth;
    const height = settings.height || window.innerHeight;

    // Create a video element to capture the frame
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    // Wait for the video to be ready
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => {
        video.play().then(resolve).catch(reject);
      };
      video.onerror = () => reject(new Error('Failed to load video stream'));
      // Timeout after 5 seconds
      setTimeout(() => reject(new Error('Video load timeout')), 5000);
    });

    // Small delay to ensure frame is rendered
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Create canvas and capture the frame
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Draw the current video frame to canvas
    ctx.drawImage(video, 0, 0, width, height);

    // Convert to blob and data URL
    const dataUrl = canvas.toDataURL('image/png', 0.92);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Failed to create blob'))),
        'image/png',
        0.92
      );
    });

    return {
      blob,
      dataUrl,
      width,
      height,
      diagnostics: {
        captureMethod: 'standard',
        preflightPassed: true,
        postflightPassed: true,
        warnings: ['Captured using native Screen Capture API'],
        pageInfo: {
          url: window.location.href,
          title: document.title,
          doctype: document.doctype ? `<!DOCTYPE ${document.doctype.name}>` : 'none',
          scrollPosition: { x: window.scrollX, y: window.scrollY },
          viewportSize: { width: window.innerWidth, height: window.innerHeight },
          documentSize: {
            width: document.documentElement.scrollWidth,
            height: document.documentElement.scrollHeight,
          },
          bodyStyles: {},
          htmlStyles: {},
        },
        timing: { preflightMs: 0, captureMs: 0, postflightMs: 0, totalMs: 0 },
        errors: [],
      },
    };
  } finally {
    // Always stop all tracks to release the screen capture
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }
}

/**
 * Check if native Screen Capture API is available
 * Returns false on iOS/Android devices even if API exists, since it doesn't work
 */
export function isNativeScreenCaptureSupported(): boolean {
  // Mobile devices don't support getDisplayMedia
  if (isMobileDevice()) {
    return false;
  }
  return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
}
